import axios from "axios"
import type {
  // Add new types for Third Party Eligibility API
  CheckEligibilityRequest,
  CheckEligibilityResponse,
  ConfirmOtpRequest,
  ConfirmOtpResponse,
  ConfirmThirdPartyOtpRequest,
  ConfirmThirdPartyOtpResponse,
  ResendOtpRequest,
  ResendOtpResponse,
  StartClaimProcessRequest,
  StartClaimProcessResponse,
  StartPolicyReviewRequest,
  StartPolicyReviewResponse,
} from "./hcsraCustomApi"

const API_BASE_PATH = "/api/hcsra-proxy"
const API_URL = process.env.API_URL || "https://api-dev.hcsra.co.il"

/**
 * Client-side API module that communicates with the server-side API proxy
 * instead of directly with the external API
 */

/**
 * Initiates a policy review eligibility check and sends an OTP to the provided contact information
 * Uses server-side proxy to make the actual external API call
 */
export const startPolicyReviewEligibility = async (
  requestData: StartPolicyReviewRequest,
): Promise<StartPolicyReviewResponse> => {
  try {
    const response = await axios.post(
      `${API_BASE_PATH}/policy-review-eligibility/StartPolicyReviewEligibilityProcess`,
      {
        request: requestData,
      },
    )
    return response.data
  } catch (error) {
    console.error("Error starting policy review eligibility process:", error)
    throw error
  }
}

/**
 * Verifies the OTP code entered by the user
 * Uses server-side proxy to make the actual external API call
 */
export const confirmTwoFactorAuthentication = async (
  requestData: ConfirmOtpRequest,
): Promise<ConfirmOtpResponse> => {
  try {
    const response = await axios.post(
      `${API_BASE_PATH}/policy-review-eligibility/ConfirmTwoFactorAuthentication`,
      {
        request: requestData,
      },
    )
    return response.data
  } catch (error) {
    console.error("Error confirming two-factor authentication:", error)
    throw error
  }
}

/**
 * Requests a new OTP code to be sent to the user
 * Uses server-side proxy to make the actual external API call
 */
export const resendOtp = async (
  requestData: ResendOtpRequest,
): Promise<ResendOtpResponse> => {
  try {
    const response = await axios.post(
      `${API_BASE_PATH}/policy-review-eligibility/ResendOtp`,
      {
        request: requestData,
      },
    )
    return response.data
  } catch (error) {
    console.error("Error resending OTP:", error)
    throw error
  }
}

/**
 * Checks if a third party is eligible for making a claim based on the event date and license plate number
 * Uses server-side proxy to make the actual external API call
 */
export const checkThirdPartyEligibility = async (
  requestData: CheckEligibilityRequest,
): Promise<CheckEligibilityResponse> => {
  try {
    const response = await axios.post(
      `${API_BASE_PATH}/thirdPartyEligibility/check`,
      requestData,
    )
    return response.data
  } catch (error) {
    console.error("Error checking third party eligibility:", error)
    throw error
  }
}

/**
 * Initiates a third party eligibility claim check process and sends an OTP verification
 * Uses server-side proxy to make the actual external API call
 */
export const startThirdPartyClaimProcess = async (
  requestData: StartClaimProcessRequest,
): Promise<StartClaimProcessResponse> => {
  try {
    const response = await axios.post(
      `${API_BASE_PATH}/thirdPartyEligibility/startClaimProcess`,
      requestData,
    )
    return response.data
  } catch (error) {
    console.error("Error starting third party claim process:", error)
    throw error
  }
}

/**
 * Verifies the OTP entered by the user for third party eligibility claim process
 * Uses server-side proxy to make the actual external API call
 */
export const confirmThirdPartyOtp = async (
  requestData: ConfirmThirdPartyOtpRequest,
): Promise<ConfirmThirdPartyOtpResponse> => {
  try {
    const response = await axios.post(
      `${API_BASE_PATH}/thirdPartyEligibility/confirmOtp`,
      requestData,
    )
    return response.data
  } catch (error) {
    console.error("Error confirming third party OTP:", error)
    throw error
  }
}

/**
 * Generic function to make a POST request to any Hachshara API endpoint through the proxy
 */
export const postToHcsraApi = async <TRequest, TResponse>(
  endpoint: string,
  requestData: TRequest,
): Promise<TResponse> => {
  try {
    const response = await axios.post(`${API_BASE_PATH}/${endpoint}`, {
      request: requestData,
    })
    return response.data
  } catch (error) {
    console.error(`Error in API call to ${endpoint}:`, error)
    throw error
  }
}

/**
 * Generic function to make a GET request to any Hachshara API endpoint through the proxy
 */
export const getFromHcsraApi = async <TResponse>(
  endpoint: string,
  queryParams?: Record<string, string | number | boolean>,
): Promise<TResponse> => {
  try {
    const response = await axios.get(`${API_BASE_PATH}/${endpoint}`, {
      params: queryParams,
    })
    return response.data
  } catch (error) {
    console.error(`Error in API call to ${endpoint}:`, error)
    throw error
  }
}

export const fetchSubjects = async () => {
  try {
    const res = await fetch(`${API_URL}/api/ContactUs/domains`)
    const data = await res.json()
    const normelaizedData = normalizedData(data.body.domains)
    return normelaizedData
  } catch (error) {}
}

export const normalizedData = (domainsApiDate: any[]) => {
  const domains = domainsApiDate.map((domain) => {
    return {
      // domainCode: domain.domainCode,
      value: domain.domainCode,
      caption: domain.domainDescription,
    }
  })
  const subDomains = domainsApiDate.reduce((acc, curr) => {
    const currentItems = curr.subDomains.map((subDomain: any) => ({
      parentCode: curr.domainCode,
      value: subDomain.subDomainCode,
      caption: subDomain.subDomainDescription,
    }))
    return [...acc, ...currentItems]
  }, [])

  return { domainCode: domains, subDomainCode: subDomains }
}
